import {FC, useEffect, useRef, useState} from 'react'
import { useLocation } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useIntl } from 'react-intl'

import toastr from 'toastr';

import { MarkdownWithLinks } from '../../../components/MarkdownWithLinks'

import Recorder from "recorder-js";

import { useAuth } from '../../../context/auth';
import { useChat } from '../../../context/chat';
import { determineDirection } from '../../../utils';

import { ChatMessageGenerationType, ChatUploadedFile, ChatUploadedFileType, MEDIA_UNDERSTANDING_TYPES, FILE_TYPE_OF_GENERATION_TYPE, GENERATION_TYPE_OF_FILE_TYPE } from '../../../models';

import { chatAPI } from '../../../api';

import UploadedFile, {getVideoThumbnailFromURL} from '../../../components/UploadedMediaModal';

import { useLang } from '../../../../_metronic/i18n/Metronici18n';
import { MenuComponent } from '../../../../_metronic/assets/ts/components';


interface Props {
  showToBottomButton: boolean
  scrollToBottom: () => void
  currentGenerationType: ChatMessageGenerationType
  setCurrentGenerationType: (type: ChatMessageGenerationType) => void
  currentUploadedFiles: ChatUploadedFile[]
  setCurrentUploadedFiles: ( images: ChatUploadedFile[] ) => void
  isLoadingChatMessages: boolean
  isSending: boolean
  isTyping: boolean
  stopTyping: () => void
  sendMessage: (message: string, type: ChatMessageGenerationType, needDeepThinking: boolean) => void
}

const UploadedFile_ = ({ uploadedFile, index, handleRemove }: { uploadedFile: ChatUploadedFile; index: number, handleRemove: () => void }) => {
  return <div className={`position-relative mb-2 ${uploadedFile.type !== ChatUploadedFileType.AUDIO && 'border rounded shadow'} me-3 h-50px`}>
    <UploadedFile
      uploadedFile={uploadedFile}
      handleRemove={handleRemove}
    />
  </div>
}

const ChatFooter: FC<Props> = ({showToBottomButton, scrollToBottom, currentGenerationType, setCurrentGenerationType, currentUploadedFiles, setCurrentUploadedFiles, isLoadingChatMessages, isSending, isTyping, stopTyping, sendMessage }) => {

  const intl = useIntl()

  const location = useLocation()

  const lang = useLang()
  const isRTL = lang === 'ar'

  const isSharedChat = location.pathname.includes('/s/')

  const messageInputRef = useRef<HTMLTextAreaElement | null>(null)
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const videoInputRef = useRef<HTMLInputElement | null>(null)
  const audioInputRef = useRef<HTMLInputElement | null>(null)

  const { state : { currentChat } } = useChat()

  const {currentUser} = useAuth()

  const currentSettings = currentUser?.settings

  const [hasMessage, setHasMessage] = useState<boolean>(false)

  const [automaticallySelectedGenerationType, setAutomaticallySelectedGenerationType] = useState<boolean>(false)
  const [alreadyAutomaticallySwitchedGenerationType, setAlreadyAutomaticallySwitchedGenerationType] = useState<boolean>(false)
  const [previouslySelectedGenerationType, setPreviouslySelectedGenerationType] = useState<ChatMessageGenerationType>(currentGenerationType)


  const [rows, setRows] = useState(1)
  const maxRows = 5
  const mediaUploadEnabled = [currentSettings?.prompt_setting?.image_understanding, currentSettings?.prompt_setting?.video_understanding, currentSettings?.prompt_setting?.transcription]
  const mediaHandlingTypes = [ChatMessageGenerationType.IMAGE_UNDERSTANDING, ChatMessageGenerationType.VIDEO_UNDERSTANDING, ChatMessageGenerationType.TRANSCRIPTION]
  const promptFeatures = [
    {
      icon: 'fa-solid fa-paperclip rotate-135', 
      type: ChatMessageGenerationType.MEDIA_UNDERSTANDING, 
      sub_types: mediaUploadEnabled.map((enabled, index) => enabled ? mediaHandlingTypes[index] : null).filter(Boolean),
      enabled: mediaUploadEnabled.filter(Boolean).length > 0
    },
    {icon: 'fa-solid fa-comments', type: ChatMessageGenerationType.TEXT, enabled: true},
    {icon: 'fa-solid fa-lightbulb', type: ChatMessageGenerationType.DEEP_THINKING, enabled: currentSettings?.content_setting?.deep_thinking && currentSettings?.prompt_setting?.deep_thinking},
    {icon: 'fa-solid fa-image', type: ChatMessageGenerationType.IMAGE, enabled: currentSettings?.prompt_setting?.image_generation},
    {icon: 'fa-solid fa-music', type: ChatMessageGenerationType.MUSIC, enabled: currentSettings?.prompt_setting?.music_generation},
    {icon: 'fa-solid fa-volume-high', type: ChatMessageGenerationType.SPEECH, enabled: currentSettings?.prompt_setting?.text_to_speech}
  ]
  const enabledPromptFeatures = promptFeatures.filter((feature) => feature.enabled)

  useEffect(() => {
    if (messageInputRef.current && !isLoadingChatMessages && !isTyping) {
      messageInputRef.current.focus();
    }
  }, [currentChat, isLoadingChatMessages, isSending, isTyping]);

  useEffect(() => {
    MenuComponent.createInsance("#media-understanding-menu-dropdown");
  }, [enabledPromptFeatures, currentGenerationType, currentUploadedFiles])

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textareaLineHeight = 24
    const previousRows = event.target.rows
    event.target.rows = 1

    const currentRows = Math.floor(event.target.scrollHeight / textareaLineHeight)

    if (currentRows === previousRows) {
        event.target.rows = currentRows
    }

    if (currentRows >= maxRows) {
        event.target.rows = maxRows
        event.target.scrollTop = event.target.scrollHeight
    }

    setRows(currentRows < maxRows ? currentRows : maxRows)
    setCurrentMessageInput();
  }    

  const onKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
	  setCurrentMessageInput();
  }

  const isImageGenerationInput = (message: string) => {
    const imageKeywords = currentSettings?.prompt_setting?.image_generation_triggering_terms.split(',').map(keyword => keyword);
    return imageKeywords?.some(keyword => message.trim().toLowerCase().startsWith(keyword.trim().toLowerCase())) ?? false;
  }
  
  const setCurrentMessageInput = () => {
    if( messageInputRef.current ){
      const message = messageInputRef.current.value.trim()
      setHasMessage(message != '');
      if( currentSettings?.prompt_setting?.image_generation && currentSettings?.prompt_setting?.image_generation_triggering_terms && currentSettings?.prompt_setting?.image_generation_triggering_terms.trim() != '' ){
        if( message === ''){
          setAlreadyAutomaticallySwitchedGenerationType(false)
        }
        if( isImageGenerationInput(message) && ![ChatMessageGenerationType.IMAGE, ChatMessageGenerationType.IMAGE_UNDERSTANDING].includes(currentGenerationType) && !alreadyAutomaticallySwitchedGenerationType ){
          setPreviouslySelectedGenerationType(currentGenerationType)
          setCurrentGenerationType(ChatMessageGenerationType.IMAGE)
          setAutomaticallySelectedGenerationType(true)
          setAlreadyAutomaticallySwitchedGenerationType(true)
        }
        if( message === '' && automaticallySelectedGenerationType ){
          setCurrentGenerationType(previouslySelectedGenerationType)
          setAutomaticallySelectedGenerationType(false)
        }
      }
    }
  }

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.shiftKey === false ) {
	    e.preventDefault()
      handleSendMessage()
    }
  }
  
  const handleSendMessage = () => {
    if( (messageInputRef.current && messageInputRef.current?.value.trim() != '') || currentGenerationType === ChatMessageGenerationType.TRANSCRIPTION ){
      sendMessage(
        messageInputRef.current ? messageInputRef.current.value : '', 
        currentGenerationType === ChatMessageGenerationType.DEEP_THINKING ? ChatMessageGenerationType.TEXT : currentGenerationType, 
        currentGenerationType === ChatMessageGenerationType.DEEP_THINKING
      )
      if( messageInputRef.current ){
        messageInputRef.current.value = ""
      }
      setRows(1)
      setHasMessage(false)
      setAutomaticallySelectedGenerationType(false)
      setAlreadyAutomaticallySwitchedGenerationType(false)
    }
  }


  const [recorder, setRecorder] = useState<Recorder | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [STTStatus, setSTTStatus] = useState<string | null>(null);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  
  
  const initRecorder = async () => {
    try{
      const audioContext = new window.AudioContext();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const newRecorder = new Recorder(audioContext, { type: "audio/wav" });
      await newRecorder.init(stream);
      setRecorder(newRecorder);
      setMediaStream(stream);
      
    }catch (error:any) {
      if (error.name === 'NotAllowedError') {
        toastr.error(intl.formatMessage({id: 'chat.message.input.microphone_permission'}))
      } else if (error.name === 'NotFoundError') {
        toastr.error(intl.formatMessage({id: 'chat.message.input.microphone_permission'}))
      } else if (error.name === 'AbortError') {
        toastr.error(intl.formatMessage({id: 'chat.message.input.microphone_permission'}))
      } else {
        toastr.error(intl.formatMessage({id: 'chat.message.input.microphone_permission'}))
      }
      setSTTStatus(null)
    }
  };

  const startRecording = async () => {
    if (!recorder) return;
    setSTTStatus('recording')
    await recorder.start();
    const id = setTimeout(() => {
      setSTTStatus('stop_recording')
    }, 20000);
    setTimerId(id);
  };

  const stopRecording = async () => {
    if (!recorder || !mediaStream) return;
    const { blob } = await recorder.stop();
    mediaStream.getTracks().forEach(track => track.stop());
    setSTTStatus('processing')
    sendAudio(blob);
    setRecorder(null);
    setMediaStream(null);
  };

  const sendAudio = async (blob) => {
    try {
      const text = await chatAPI.recognizeSpeech(currentChat?.id || 'new', blob);
      if (text && messageInputRef.current) {
        messageInputRef.current.value += text;
        setCurrentMessageInput();
      }
    } catch (error:any) {
    }
    setSTTStatus(null)
  };

  useEffect(() => {
    if( recorder ){
      startRecording()
    }
  }, [recorder]);

  useEffect(() => {
    if (timerId) {
      clearTimeout(timerId);
      setTimerId(null);
    }
    if (STTStatus === 'init') {
      initRecorder();
    } else if( recorder && STTStatus === 'stop_recording' ){
      stopRecording();
    }
  }, [STTStatus]);

  const GenerationTypeButton = ({
    placement,
    iconClass,
    currentGenerationType,
    generationType,
    subGenerationTypes
  }) => {
    return (
      <div>
        <OverlayTrigger
          placement={placement}
          overlay={<Tooltip>{intl.formatMessage({id: `chat.message.input.generate.${subGenerationTypes?.length === 1 ? subGenerationTypes[0] : generationType}`})}</Tooltip>}
        >
          <a
            className={`btn btn-icon btn-sm text-hover-primary`}
            data-kt-menu-trigger={generationType === ChatMessageGenerationType.MEDIA_UNDERSTANDING && subGenerationTypes?.length > 1 ? "click" : undefined}
            data-kt-menu-attach={generationType === ChatMessageGenerationType.MEDIA_UNDERSTANDING && subGenerationTypes?.length > 1 ? "parent" : undefined}
            data-kt-menu-placement={generationType === ChatMessageGenerationType.MEDIA_UNDERSTANDING && subGenerationTypes?.length > 1 ? `top-end` : undefined}
            onClick={() => {
              if( generationType === ChatMessageGenerationType.MEDIA_UNDERSTANDING ){
                if( subGenerationTypes.length  === 1 ){
                  if( subGenerationTypes[0] === ChatMessageGenerationType.IMAGE_UNDERSTANDING && imageInputRef.current) {
                    imageInputRef.current.click();
                  }else if( subGenerationTypes[0] === ChatMessageGenerationType.VIDEO_UNDERSTANDING && videoInputRef.current) {
                    videoInputRef.current.click();
                  }else if( subGenerationTypes[0] === ChatMessageGenerationType.TRANSCRIPTION && audioInputRef.current) {
                    audioInputRef.current.click();
                  }
                }
              }else{
                setCurrentGenerationType(generationType);
                setAutomaticallySelectedGenerationType(false);
                setCurrentUploadedFiles([]);
              }
            }}
          >
            <i className={`${iconClass} ${(generationType === currentGenerationType || (generationType === ChatMessageGenerationType.MEDIA_UNDERSTANDING && MEDIA_UNDERSTANDING_TYPES.includes(currentGenerationType))) && 'text-primary'}`}></i>
          </a>
        </OverlayTrigger>
        
        { generationType === ChatMessageGenerationType.MEDIA_UNDERSTANDING && subGenerationTypes && subGenerationTypes.length > 1 && (
          <div 
            className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-color fw-semibold py-2 fs-6 w-200px" 
            data-kt-menu="true"
            id="media-understanding-menu-dropdown"
          >
            {subGenerationTypes.map((generationType) => {
              const fileType = FILE_TYPE_OF_GENERATION_TYPE[generationType]
              return (
                <div className="menu-item px-3 my-0" key={'media-understanding-menu-'+fileType}>
                  <a className={`menu-link px-3 py-2`} onClick={()=>{
                    if (fileType === ChatUploadedFileType.IMAGE && imageInputRef.current) {
                      imageInputRef.current.click();
                    } else if (fileType === ChatUploadedFileType.VIDEO && videoInputRef.current) {
                      videoInputRef.current.click();
                    } else if (fileType === ChatUploadedFileType.AUDIO && audioInputRef.current) {
                      audioInputRef.current.click();
                    }
                  }}>
                    <span className="menu-icon">
                      <i className={`fa-solid fa-${fileType === ChatUploadedFileType.AUDIO ? 'volume-high' : fileType }`}></i>
                    </span>
                    <span className="menu-title">
                      {intl.formatMessage({id: `chat.message.input.generate.${generationType}`})}
                    </span>
                  </a>
                </div>
              );
            })}
          </div>
        )}

        {generationType === ChatMessageGenerationType.MEDIA_UNDERSTANDING && (
          subGenerationTypes.map((generationType) => {
            const fileType = FILE_TYPE_OF_GENERATION_TYPE[generationType];
            const ref = {
              [ChatUploadedFileType.IMAGE]: imageInputRef,
              [ChatUploadedFileType.VIDEO]: videoInputRef,
              [ChatUploadedFileType.AUDIO]: audioInputRef,
            }[fileType];
            const accept = currentSettings?.prompt_setting?.uploadable_file_extension?.[generationType]?.map((v) => '.' + v).join(',');
            const maxFiles = getMaxFiles(generationType);
            return (
              <input
                key={'upload-file-'+fileType}
                type="file"
                name={fileType}
                ref={ref}
                multiple={maxFiles > 1}
                accept={accept}
                className="d-none"
                onClick={(e) => {
                  if (currentUploadedFiles.filter((f) => f.type == fileType).length === maxFiles) {
                    toastr.error(
                      intl.formatMessage({ id: `chat.message.input.max_${fileType}_upload` }, { max: maxFiles })
                    );
                    e.preventDefault();
                  }
                }}
                onChange={handleFileUpload}
              />
            );
          })
        )}
      </div>
    );
  };

  const getMaxFiles = (generationType: ChatMessageGenerationType) => {
    if (currentSettings?.prompt_setting?.[`${generationType}_max_files`] == null) {
      return 0
    }
    return currentSettings?.prompt_setting?.[`${generationType}_max_files`]
  }

  const getMaxFileSize = (generationType: ChatMessageGenerationType) => {
    if (currentSettings?.prompt_setting?.[`${generationType}_max_file_size`] == null) {
      return 0
    }
    return currentSettings?.prompt_setting?.[`${generationType}_max_file_size`];
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileType = e.target.name
    const generationType = GENERATION_TYPE_OF_FILE_TYPE[fileType]
    const max_files = getMaxFiles(generationType)
    const max_file_size_in_mb = getMaxFileSize(generationType)
    const max_file_size = max_file_size_in_mb * 1024 * 1024

    if (currentUploadedFiles.filter((f) => f.type == fileType).length === max_files) {
      toastr.error(intl.formatMessage({id: `chat.message.input.max_${fileType}_upload`}, {max: max_files}))
      return
    }
    
    const files = e.target.files;
    if (files) {
      const validFiles = Array.from(files)
        .slice(0, max_files - currentUploadedFiles.filter((f) => f.type == fileType).length)
        .filter(file => {
          if (file.size > max_file_size) {
            toastr.error(intl.formatMessage({ id: `chat.message.input.max_${fileType}_size` }, { max: max_file_size_in_mb }));
            return false;
          }
          return true;
        });

      const fileReadPromises = validFiles.map(file => {
        return new Promise<ChatUploadedFile>(async (resolve, reject) => {
          try {
            const objectUrl = URL.createObjectURL(file);
            const uploadedFile: ChatUploadedFile = {
              type: fileType as ChatUploadedFileType,
              objectUrl: objectUrl,
              "file": file,
            };
            if (fileType === ChatUploadedFileType.VIDEO) {
              try {
                const thumbnailUrl = await getVideoThumbnailFromURL(objectUrl);
                resolve({ ...uploadedFile, thumbnailBase64Url: thumbnailUrl });
              } catch (thumbErr) {
                console.warn('Failed to extract thumbnail', thumbErr);
                resolve(uploadedFile); // fallback without thumbnail
              }
            } else {
              resolve(uploadedFile);
            }
          } catch (err) {
            reject(err);
          }
        });
      });

      try {
        const newlyAddingFiles = await Promise.all(fileReadPromises);
        if (newlyAddingFiles.length > 0) {
          if ( currentGenerationType !== generationType ){
            setCurrentUploadedFiles(newlyAddingFiles);
          }else{
            setCurrentUploadedFiles([...currentUploadedFiles, ...newlyAddingFiles]);
          }
          setCurrentGenerationType(generationType);
          messageInputRef.current?.focus();
          if (imageInputRef.current) {
            imageInputRef.current.value = '';
          }
        }
      } catch (err) {
        console.error('Error reading files', err);
      }
    }
  }

  return  (
    <div id="chat-footer" className="chat-footer w-100 w-xl-800px card-footer p-2 rounded-0 border-0 position-relative">
      {showToBottomButton && (
        <button className='btn btn-icon btn-circle btn-secondary position-absolute z-index-1 start-50 ms-n6 ms-md-n7 mt-n18' onClick={scrollToBottom}>
          <i className="fa-solid fa-arrow-down fs-4"></i>
        </button>
      )}
      {!isSharedChat && 
      <div className="d-flex flex-column mb-1 rounded p-2 bg-body border border-gray-400 bg-opacity-50" id="chat-message-input-area">
        <div className="d-flex flex-stack">
          <div className="d-flex flex-column flex-grow-1">
            { currentUploadedFiles.length > 0 && (
              <div className="d-flex flex-row px-5 pt-5">
                { currentUploadedFiles.map((uploadedFile, index) => (
                  uploadedFile.objectUrl && <UploadedFile_ 
                    key={`chat-footer-uploaded-image-${index}`}
                    uploadedFile={uploadedFile} 
                    index={index} 
                    handleRemove={() => {
                    setCurrentUploadedFiles(currentUploadedFiles.filter((_, i) => i !== index))
                      if( currentUploadedFiles.length === 1 ){
                        setCurrentGenerationType(ChatMessageGenerationType.TEXT)
                      }
                    }}
                  />
                ))}
              </div>
            )}
            <div className="d-flex flex-column flex-start">
              <textarea 
                ref={messageInputRef} 
                className="form-control form-control-flush chat-message-textarea" 
                spellCheck="false" 
                data-kt-element="input" 
                rows={rows} 
                placeholder={intl.formatMessage({id: `chat.message.input.placeholder.${currentGenerationType}`})}
                onKeyPress={onEnterPress}
                maxLength={10000}
                onKeyUp={onKeyUp}
                onInput={handleInput}
                disabled={isSending || isLoadingChatMessages || isTyping || currentGenerationType === ChatMessageGenerationType.TRANSCRIPTION }
                style={messageInputRef.current?.value ? {direction: determineDirection(messageInputRef.current?.value)} : {}} 
              >
              </textarea>
            </div>
          </div>
          {STTStatus === 'processing' &&
            (
              <span className='spinner-border spinner-border-sm align-middle me-4'></span>
            )
          }
        </div>
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-row">
            {currentSettings && enabledPromptFeatures.length > 1 && enabledPromptFeatures.map((feature, index) => (
              <GenerationTypeButton
                placement={index === 0 ? (isRTL ? 'right' : 'left') : index === enabledPromptFeatures.length - 1 ? (isRTL ? 'left' : 'right') : 'top'}
                iconClass={feature.icon}
                currentGenerationType={currentGenerationType}
                generationType={feature.type}
                subGenerationTypes={feature.sub_types}
              />
            ))}
          </div>
          <div className="d-flex">
          {
          STTStatus !== 'processing' &&
          (
            <>
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip>{intl.formatMessage({id: 'chat.message.input.send'})}</Tooltip>}
            >
              <button className={`btn btn-icon btn-sm text-hover-dark ${!hasMessage && currentGenerationType !== ChatMessageGenerationType.TRANSCRIPTION && 'disabled'} ${isSending || isTyping || STTStatus ? 'd-none' : 'd-md-block'}`} onClick={handleSendMessage}>
                <i className="fa-solid fa-paper-plane fs-5"></i>
              </button>
            </OverlayTrigger>
            { currentSettings?.prompt_setting?.speech_to_text && (
              STTStatus === 'recording' || STTStatus === 'init'
              ?
              (
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{intl.formatMessage({id: `chat.message.input.stop_typing_by_voice`})}</Tooltip>}
              >
                <button className='btn btn-icon btn-sm d-md-block' 
                  onClick={() => setSTTStatus('stop_recording')}
                >
                  <i className="fa-solid fa-circle-dot text-danger fs-5"></i>
                </button>
              </OverlayTrigger>
              )
              :
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{intl.formatMessage({id: `chat.message.input.type_by_voice`})}</Tooltip>}
              >
                <button className={`btn btn-icon btn-sm text-hover-primary text-gray-400 ${isSending || isTyping ? 'd-none' : 'd-md-block'}`}
                  onClick={() => {
                    setSTTStatus('init')
                  }}
                >
                  <i className="fa-solid fa-microphone fs-5"></i>
                </button>
              </OverlayTrigger>
            )}
                  
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip>{intl.formatMessage({id: 'chat.message.input.stop_typing'})}</Tooltip>}
            >
              <button className={`btn btn-icon btn-sm text-hover-dark ${isSending && 'disabled'} ${(!isSending && !isTyping) && 'd-none'}`} onClick={stopTyping}>
                <i className="fa-solid fa-stop-circle text-primary fs-5"></i>
              </button>
            </OverlayTrigger>
            </>
          )}
          </div>
        </div>
      </div>
      }
      <div className={`flex-column flex-${isSharedChat ? 'center': 'start d-none d-md-flex'}`}>
        {!isSharedChat &&  <div className="mb-n3"><MarkdownWithLinks asBlank={!currentUser?.email}>{intl.formatMessage({id: 'chat.message.input.agreement'})}</MarkdownWithLinks></div>}
        <div className="mb-n5 text-center"><MarkdownWithLinks asBlank={!currentUser?.email}>{intl.formatMessage({id: 'chat.message.input.incorrect'})}</MarkdownWithLinks></div>
      </div>
    </div>
  )
}

export { ChatFooter };